import { useState, useEffect, SyntheticEvent } from "react";
import { toast } from "react-toastify";
import { useSWRConfig } from "swr";
import { useNavigate } from "react-router";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCreateProject, useUpdateProject } from "@/hooks/useProjects";
import { Project, RawProject } from "@/types/project";
import { ScrollArea } from "../ui/scroll-area";

interface FormData {
  name: string;
  description: string;
  start_date: string;
  end_date: string;
  funding: string;
  wbs: string;
  learning_goal: string;
}

interface ProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "create" | "edit";
  project?: Project;
}

interface CreateProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface EditProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  project: Project;
}

export function ProjectDialog({ open, onOpenChange, mode, project }: ProjectDialogProps) {
  const { mutate } = useSWRConfig();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<FormData>({
    name: "",
    description: "",
    start_date: "",
    end_date: "",
    funding: "",
    wbs: "",
    learning_goal: "",
  });

  const { trigger: createProject, isMutating: isCreating } = useCreateProject();
  const { trigger: updateProject, isMutating: isUpdating } = useUpdateProject();

  const fundingOptions = ["MPW", "EU Horizon", "Internal", "Customer", "UNKNOWN"];

  // Initialize form data when project changes (for edit mode)
  useEffect(() => {
    if (mode === "edit" && project) {
      setFormData({
        name: (project.name || "").toUpperCase(),
        description: project.description || "",
        start_date: project.startDate ? project.startDate.toISOString().split("T")[0] : "",
        end_date: project.endDate ? project.endDate.toISOString().split("T")[0] : "",
        funding: project.funding || "",
        wbs: project.wbs || "",
        learning_goal: project.learningGoal || "",
      });
    } else {
      // Reset form for create mode
      setFormData({
        name: "",
        description: "",
        start_date: "",
        end_date: "",
        funding: "",
        wbs: "",
        learning_goal: "",
      });
    }
  }, [mode, project, open]);

  const handleSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error("Project name is required");
      return;
    }

    if (!formData.wbs.trim()) {
      toast.error("WBS is required");
      return;
    }

    const projectData = {
      name: formData.name.trim().toUpperCase(),
      description: formData.description.trim() || null,
      start_date: formData.start_date || null,
      end_date: formData.end_date || null,
      funding: formData.funding || "UNKNOWN",
      wbs: formData.wbs.trim(),
      learning_goal: formData.learning_goal.trim() || null,
    };

    if (mode === "create") {
      await createProject(projectData, {
        onSuccess: (project: RawProject) => {
          navigate(`/projects/${project.id}`);
          mutate({ url: "/projects" });
        },
        onError: (error) => {
          toast.error(`Failed to create project: ${error.message}`);
        },
      });
    } else {
      // Edit mode
      await updateProject(
        {
          ...projectData,
          id: project!.id.toString(),
        },
        {
          onSuccess: () => {
            toast.success("Project updated successfully!");
            mutate({ url: "/projects" });
            onOpenChange(false);
          },
          onError: (error) => {
            toast.error(`Failed to update project: ${error.message}`);
          },
        },
      );
    }
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const isLoading = isCreating || isUpdating;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 sm:max-w-[425px]">
        <form onSubmit={handleSubmit} className="flex flex-col gap-0 max-h-[90vh]">
          <DialogHeader className="contents space-y-0 text-left">
            <ScrollArea className="flex max-h-full flex-col overflow-hidden">
              <DialogTitle className="px-6 pt-6">
                {mode === "create" ? "Create New Project" : "Edit Project"}
              </DialogTitle>
              <DialogDescription className="p-6">
                {mode === "create"
                  ? "Fill in the project details to create a new project. You will be set as the project owner."
                  : "Update the project details below."}
              </DialogDescription>
              <div className="px-6 pb-6 space-y-2">
                <div className="space-y-2">
                  <Label htmlFor="name">
                    Project Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    placeholder="Enter project name"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => handleInputChange("description", e.target.value)}
                    placeholder="Enter project description"
                    rows={3}
                  />
                </div>
                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start_date">Start Date</Label>
                    <Input
                      id="start_date"
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => handleInputChange("start_date", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="end_date">End Date</Label>
                    <Input
                      id="end_date"
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => handleInputChange("end_date", e.target.value)}
                      min={formData.start_date || undefined}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="funding">Funding</Label>
                    <Select
                      value={formData.funding}
                      onValueChange={(value) => handleInputChange("funding", value)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select funding type" />
                      </SelectTrigger>
                      <SelectContent>
                        {fundingOptions.map((funding) => (
                          <SelectItem key={funding} value={funding}>
                            {funding === "UNKNOWN" ? "Unknown" : funding}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="wbs">
                      WBS <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="wbs"
                      type="text"
                      value={formData.wbs}
                      onChange={(e) => handleInputChange("wbs", e.target.value)}
                      placeholder="Enter WBS"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="learning_goal">Learning Goal</Label>
                    <Textarea
                      id="learning_goal"
                      value={formData.learning_goal}
                      onChange={(e) => handleInputChange("learning_goal", e.target.value)}
                      placeholder="Enter learning goal"
                      rows={3}
                    />
                  </div>
                </div>
              </div>
            </ScrollArea>
          </DialogHeader>
          <DialogFooter className="flex-row items-center justify-end border-t px-6 py-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading
                ? mode === "create"
                  ? "Creating..."
                  : "Updating..."
                : mode === "create"
                  ? "Create Project"
                  : "Update Project"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Wrapper component for creating projects
export function CreateProjectDialog({ open, onOpenChange }: CreateProjectDialogProps) {
  return <ProjectDialog open={open} onOpenChange={onOpenChange} mode="create" />;
}

// Wrapper component for editing projects
export function EditProjectDialog({ open, onOpenChange, project }: EditProjectDialogProps) {
  return <ProjectDialog open={open} onOpenChange={onOpenChange} mode="edit" project={project} />;
}
